import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import PortalAdminCredentialsModal from '../../components/PortalAdminCredentialsModal';
import { useNotification } from '../../context/NotificationContext';
import { getBrands, createBrand, updateBrand, deleteBrand, getBrandAdmin, upsertBrandAdmin } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import { resolveUrlForImageLoad } from '../../utils/adminMediaUrls';
import ExpandableText from '../../components/ExpandableText';
import { getUpiIdError } from '../../utils/upiValidation';
import { getTenDigitPhoneError, toTenDigitPhone } from '../../utils/phone';
import usePortalScope from '../../hooks/usePortalScope';
import { removeConfirmMessage } from '../../utils/portalCopy';
import AdminFormSection from '../../components/AdminFormSection';
import { useAuth } from '../../context/AuthContext';
import useFocusTrap from '../../hooks/useFocusTrap';
import './AdminPages.css';

const BrandManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const { refreshPortalContext } = useAuth();
  const portal = usePortalScope();
  const canManageAdmins = portal.canManageAdmins;
  const canCreateBrand = portal.canCreateBrand;
  const canDeleteBrand = portal.canDeleteBrand;
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [adminTarget, setAdminTarget] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [formData, setFormData] = useState({
    brandCode: '',
    name: '',
    logo: '',
    description: '',
    contact: { phone: '', email: '', website: '' },
    address: { street: '', city: '', state: '', zipCode: '', country: '' },
    payment: { upiId: '', accountName: '', accountNumber: '', ifscCode: '' }
  });
  const MAX_BRAND_NAME = 250;
  const [formErrors, setFormErrors] = useState({});
  const cancelRef = useRef(() => {});
  const onEscapeBrand = useCallback(() => cancelRef.current(), []);
  const formRef = useFocusTrap(showForm, { onEscape: onEscapeBrand });

  const searchKey = useMemo(() => JSON.stringify({ searchTerm }), [searchTerm]);

  const getBrandParams = useCallback(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    return params;
  }, [searchTerm]);

  const { items: brands, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getBrands,
    resetKey: searchKey,
    getParams: getBrandParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(brands, (brand) => brand._id, {
    defaultView: 'grid',
    resetKey: searchKey
  });

  const suggestBrandCode = (name) => {
    const words = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const a = (words[0]?.[0] || '').toUpperCase();
    const b = (words[1]?.[0] || words[0]?.[1] || '').toUpperCase();
    const raw = `${a}${b}`.replace(/[^A-Z0-9]/g, '');
    return (raw || 'BC').padEnd(2, 'X').slice(0, 2);
  };

  // When the form is shown (Add or Edit), scroll it into view so the user
  // always sees the editable fields after clicking the button further below.
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showForm]);

  const validateForm = () => {
    const err = {};
    const phoneErr = getTenDigitPhoneError(formData.contact?.phone);
    if (phoneErr) err.phone = phoneErr;
    const em = String(formData.contact?.email || '').trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      err.email = 'Enter a valid email address';
    }
    const zip = String(formData.address?.zipCode || '').replace(/\D/g, '');
    if (formData.address?.zipCode && zip && zip.length !== 6) {
      err.zipCode = 'Pincode must be exactly 6 digits';
    }
    const lettersRe = /^[A-Za-z][A-Za-z\s.'-]*$/;
    for (const [key, val, label] of [
      ['city', formData.address?.city, 'City'],
      ['state', formData.address?.state, 'State'],
      ['country', formData.address?.country, 'Country']
    ]) {
      const t = String(val || '').trim();
      if (t && !lettersRe.test(t)) {
        err[key] = `${label} should contain only letters (and spaces, dots, hyphens)`;
      }
    }
    const upiErr = getUpiIdError(formData.payment?.upiId);
    if (upiErr) err.upiId = upiErr;
    setFormErrors(err);
    return !Object.values(err).some(Boolean);
  };

  const handleUpiBlur = () => {
    const upiErr = getUpiIdError(formData.payment?.upiId);
    setFormErrors((prev) => ({ ...prev, upiId: upiErr }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (String(formData.name || '').trim().length > MAX_BRAND_NAME) {
      showError(`Brand name must be at most ${MAX_BRAND_NAME} characters.`);
      return;
    }
    if (!validateForm()) {
      showError('Please fix the highlighted fields and try again.');
      window.requestAnimationFrame(() => {
        const firstInvalid = formRef.current?.querySelector('.input-error');
        firstInvalid?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        firstInvalid?.focus?.({ preventScroll: true });
      });
      return;
    }
    try {
      if (editingBrand) {
        await updateBrand(editingBrand._id, formData);
      } else {
        await createBrand(formData);
      }
      setShowForm(false);
      setEditingBrand(null);
      resetForm();
      showSuccess(editingBrand ? 'Brand updated.' : 'Brand created.');
      await refreshPortalContext();
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || `Failed to ${editingBrand ? 'update' : 'create'} brand`);
    }
  };

  const resetForm = () => {
    setFormErrors({});
    setFormData({
      brandCode: '',
      name: '',
      logo: '',
      description: '',
      contact: { phone: '', email: '', website: '' },
      address: { street: '', city: '', state: '', zipCode: '', country: '' },
      payment: { upiId: '', accountName: '', accountNumber: '', ifscCode: '' }
    });
  };

  const handleEdit = (brand) => {
    setFormErrors({});
    setEditingBrand(brand);
    setFormData({
      // Backfill for older brands created before `brandCode` existed.
      brandCode: brand.brandCode || suggestBrandCode(brand.name),
      // Clamp existing DB values to avoid breaking update/validation.
      name: String(brand.name || '').slice(0, MAX_BRAND_NAME),
      logo: brand.logo || '',
      description: brand.description || '',
      contact: {
        phone: toTenDigitPhone(brand.contact?.phone),
        email: brand.contact?.email || '',
        website: brand.contact?.website || ''
      },
      address: {
        street: brand.address?.street || '',
        city: brand.address?.city || '',
        state: brand.address?.state || '',
        zipCode: brand.address?.zipCode || '',
        country: brand.address?.country || ''
      },
      payment: {
        upiId: brand.payment?.upiId || '',
        accountName: brand.payment?.accountName || '',
        accountNumber: brand.payment?.accountNumber || '',
        ifscCode: brand.payment?.ifscCode || ''
      }
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove brand',
      message: removeConfirmMessage('brand'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deleteBrand(id);
      showSuccess('Brand removed.');
      await refreshPortalContext();
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove brand');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBrand(null);
    resetForm();
  };
  cancelRef.current = handleCancel;

  const openBrandAdmin = async (brand) => {
    setAdminTarget(brand);
    setAdminData(null);
    setAdminLoading(true);
    try {
      const res = await getBrandAdmin(brand._id);
      setAdminData(res.data?.admin || null);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to load brand admin');
      setAdminTarget(null);
    } finally {
      setAdminLoading(false);
    }
  };

  const closeBrandAdmin = () => {
    if (adminSaving) return;
    setAdminTarget(null);
    setAdminData(null);
  };

  const saveBrandAdmin = async (payload) => {
    if (!adminTarget?._id) return;
    setAdminSaving(true);
    try {
      const res = await upsertBrandAdmin(adminTarget._id, payload);
      setAdminData(res.data?.admin || null);
      showSuccess(adminData?.id ? 'Brand admin credentials updated.' : 'Brand admin created.');
      setAdminTarget(null);
      setAdminData(null);
    } catch (error) {
      throw error;
    } finally {
      setAdminSaving(false);
    }
  };

  const brandColumns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        render: (brand) => (
          <strong>
            {String(brand.name || '').length > MAX_BRAND_NAME
              ? `${String(brand.name).slice(0, MAX_BRAND_NAME)}...`
              : brand.name}
          </strong>
        )
      },
      { key: 'brandCode', label: 'Brand Code', render: (brand) => brand.brandCode || '-' },
      {
        key: 'contact',
        label: 'Contact',
        render: (brand) =>
          [brand.contact?.phone, brand.contact?.email].filter(Boolean).join(' | ') || '-'
      },
      {
        key: 'status',
        label: 'Status',
        render: (brand) => (
          <span className={`status-badge ${brand.isActive ? 'active' : 'inactive'}`}>
            {brand.isActive ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (brand) => (
          <div className="table-actions">
            {canManageAdmins && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => openBrandAdmin(brand)}>
                Admin Login
              </button>
            )}
            <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEdit(brand)}>
              Edit
            </button>
            {canDeleteBrand && (
              <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(brand._id)}>
                Remove
              </button>
            )}
          </div>
        )
      }
    ],
    [canManageAdmins, canDeleteBrand]
  );

  const brandListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && brands.length > 0 && (
        <p className="admin-infinite-end">All brands loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const brandEmptyState = (
    <div className="empty-state">
      <p>No brands found. Create your first brand!</p>
    </div>
  );

  return (
    <AdminLayout
      pageTitle="Brand Management"
      subtitle={
        pagination.total
          ? `${brands.length} of ${pagination.total} brands · scroll for more`
          : 'Manage brands and their common information'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <div className="admin-page-header-title-row">
            <h1 style={{ margin: 0 }}>Brand List</h1>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search brands by name, brand code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {canCreateBrand && (
            <button 
              type="button"
              className="btn btn-primary" 
              onClick={() => {
                setEditingBrand(null);
                resetForm();
                setShowForm(true);
              }}
            >
              + Add Brand
            </button>
          )}
        </div>

      {loading ? (
        <Loader variant="dark" fullScreen={false} />
      ) : (
        <>
        {showForm && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancel();
          }}
        >
          <div
            ref={formRef}
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="brand-form-title"
            onClick={(e) => e.stopPropagation()}
          >
        <div className="admin-card admin-modal-card">
          <h2 id="brand-form-title" style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, fontSize: '16px', color: '#FFFFFF', marginBottom: '20px' }}>
            {editingBrand ? 'Edit Brand' : 'Add Brand'}
          </h2>
          <form onSubmit={handleSubmit}>
            <AdminFormSection title="Basics" defaultOpen>
            <div className="form-row">
            <div className="form-group">
              <label>Brand Name *</label>
              <input
                type="text"
                value={formData.name}
                maxLength={MAX_BRAND_NAME}
                onChange={(e) => {
                  const name = String(e.target.value || '').slice(0, MAX_BRAND_NAME);
                  setFormData((prev) => {
                    const next = { ...prev, name };
                    // Auto-suggest only when creating OR when code is empty.
                    if (!editingBrand && !String(prev.brandCode || '').trim()) {
                      next.brandCode = suggestBrandCode(name);
                    }
                    return next;
                  });
                }}
                required
              />
              <small style={{ color: '#8189A5', fontSize: '12px' }}>
                Maximum {MAX_BRAND_NAME} characters (not words). {String(formData.name || '').length}/{MAX_BRAND_NAME}
              </small>
            </div>
            <div className="form-group">
              <label>Brand Code (2 chars)</label>
              <input
                type="text"
                value={formData.brandCode}
                onChange={(e) =>
                  setFormData({ ...formData, brandCode: (e.target.value || '').toUpperCase() })
                }
                placeholder="e.g. BC"
                maxLength={2}
                required
                disabled={Boolean(editingBrand)}
              />
              <small style={{ color: '#8189A5', fontSize: '12px' }}>
                Used to generate vendor-friendly mirror codes (e.g., BC-HSR-MIR-001).
              </small>
            </div>
            </div>

            <div className="form-group">
              <label>Logo URL</label>
              <input
                type="text"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
              />
            </div>
            </AdminFormSection>

            <AdminFormSection title="Contact" defaultOpen>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.contact.phone}
                  className={formErrors.phone ? 'input-error' : ''}
                  onChange={(e) => {
                    const digits = toTenDigitPhone(e.target.value);
                    setFormData({
                      ...formData,
                      contact: { ...formData.contact, phone: digits }
                    });
                    if (formErrors.phone) setFormErrors((prev) => ({ ...prev, phone: '' }));
                  }}
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="10-digit mobile"
                />
                {formErrors.phone && <span className="form-error-text">{formErrors.phone}</span>}
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.contact.email}
                  className={formErrors.email ? 'input-error' : ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      contact: { ...formData.contact, email: e.target.value }
                    });
                    if (formErrors.email) setFormErrors((prev) => ({ ...prev, email: '' }));
                  }}
                />
                {formErrors.email && <span className="form-error-text">{formErrors.email}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                value={formData.contact.website}
                onChange={(e) => setFormData({
                  ...formData,
                  contact: { ...formData.contact, website: e.target.value }
                })}
                placeholder="https://example.com"
              />
            </div>
            </AdminFormSection>

            <AdminFormSection title="Address" defaultOpen={false}>
            <div className="form-group">
              <label>Street</label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, street: e.target.value }
                })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.address.city}
                  className={formErrors.city ? 'input-error' : ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    });
                    if (formErrors.city) setFormErrors((prev) => ({ ...prev, city: '' }));
                  }}
                />
                {formErrors.city && <span className="form-error-text">{formErrors.city}</span>}
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={formData.address.state}
                  className={formErrors.state ? 'input-error' : ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      address: { ...formData.address, state: e.target.value }
                    });
                    if (formErrors.state) setFormErrors((prev) => ({ ...prev, state: '' }));
                  }}
                />
                {formErrors.state && <span className="form-error-text">{formErrors.state}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Zip Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit pincode"
                  value={formData.address.zipCode}
                  className={formErrors.zipCode ? 'input-error' : ''}
                  onChange={(e) => {
                    const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 6);
                    setFormData({
                      ...formData,
                      address: { ...formData.address, zipCode: digits }
                    });
                    if (formErrors.zipCode) setFormErrors((prev) => ({ ...prev, zipCode: '' }));
                  }}
                />
                {formErrors.zipCode && <span className="form-error-text">{formErrors.zipCode}</span>}
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={formData.address.country}
                  className={formErrors.country ? 'input-error' : ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      address: { ...formData.address, country: e.target.value }
                    });
                    if (formErrors.country) setFormErrors((prev) => ({ ...prev, country: '' }));
                  }}
                />
                {formErrors.country && <span className="form-error-text">{formErrors.country}</span>}
              </div>
            </div>
            </AdminFormSection>

            <AdminFormSection
              title="Payment / UPI (brand default)"
              defaultOpen={false}
              hint="Provide either UPI ID or bank details. If UPI ID is entered, bank details are optional."
            >
            <div className="form-group">
              <label>UPI ID (VPA)</label>
              <input
                type="text"
                maxLength={80}
                placeholder="e.g. brand@paytm, 9876543210@ybl"
                value={formData.payment?.upiId || ''}
                className={formErrors.upiId ? 'input-error' : ''}
                onBlur={handleUpiBlur}
                onChange={(e) => {
                  const next = String(e.target.value || '').slice(0, 80);
                  setFormData({
                    ...formData,
                    payment: { ...(formData.payment || {}), upiId: next }
                  });
                  if (formErrors.upiId) setFormErrors((prev) => ({ ...prev, upiId: '' }));
                }}
              />
              <small style={{ color: '#8189A5', fontSize: '12px' }}>Default UPI for all salons under this brand</small>
              {formErrors.upiId && <span className="form-error-text">{formErrors.upiId}</span>}
            </div>
            <div className="form-group">
              <label>Account / Payee Name</label>
              <input
                type="text"
                placeholder="Name shown on QR and receipts"
                value={formData.payment?.accountName || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  payment: { ...(formData.payment || {}), accountName: e.target.value }
                })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Bank Account Number (optional)</label>
                <input
                  type="text"
                  placeholder="Account number"
                  value={formData.payment?.accountNumber || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment: { ...(formData.payment || {}), accountNumber: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>IFSC Code (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. SBIN0001234"
                  value={formData.payment?.ifscCode || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    payment: { ...(formData.payment || {}), ifscCode: e.target.value }
                  })}
                />
              </div>
            </div>
            </AdminFormSection>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingBrand ? 'Update Brand' : 'Create Brand'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
          </div>
        </div>
      )}

      <AdminListViewControls
        viewMode={listView.viewMode}
        onViewModeChange={listView.setViewMode}
        selectedCount={listView.selectedCount}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        totalCount={brands.length}
            showSelection={false}
      />
      <AdminSelectableList
        items={brands}
        getItemId={(brand) => brand._id}
        viewMode={listView.viewMode}
            selectable={false}
        isSelected={listView.isSelected}
        onToggleItem={listView.toggleItem}
        allSelected={listView.allSelected}
        someSelected={listView.someSelected}
        onSelectAll={listView.selectAll}
        onClearSelection={listView.clearSelection}
        columns={brandColumns}
        tableClassName="brand-table"
        renderGridCard={(brand) => (
          <div className="admin-card admin-card--stretch">
            <div>
              <h3>
                {String(brand.name || '').length > MAX_BRAND_NAME
                  ? `${String(brand.name).slice(0, MAX_BRAND_NAME)}...`
                  : brand.name}
              </h3>
              {brand.brandCode && <p><strong>Brand Code:</strong> {brand.brandCode}</p>}
              {brand.logo && (
                <img
                  src={resolveUrlForImageLoad(brand.logo)}
                  alt={brand.name}
                  style={{ maxWidth: '100px', maxHeight: '100px', marginTop: '10px' }}
                />
              )}
              <p>
                <strong>Description:</strong>{' '}
                <ExpandableText text={brand.description || ''} lines={3} />
              </p>
              <p>
                <strong>Contact:</strong>{' '}
                <ExpandableText
                  text={[brand.contact?.phone, brand.contact?.email].filter(Boolean).join(' | ')}
                  lines={1}
                />
              </p>
              {brand.contact?.website && (
                <p>
                  <strong>Website:</strong>{' '}
                  <a href={brand.contact.website} target="_blank" rel="noopener noreferrer">
                    {brand.contact.website}
                  </a>
                </p>
              )}
              {brand.address?.street && (
                <p>
                  <strong>Address:</strong>{' '}
                  <ExpandableText
                    text={[
                      brand.address.street,
                      brand.address.city,
                      brand.address.state,
                      brand.address.zipCode,
                      brand.address.country
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    lines={2}
                  />
                </p>
              )}
              {brand.payment?.upiId && (
                <p>
                  <strong>UPI ID:</strong>{' '}
                  <ExpandableText
                    text={`${brand.payment.upiId}${brand.payment.accountName ? ` (${brand.payment.accountName})` : ''}`}
                    lines={1}
                  />
                </p>
              )}
              <p><strong>Status:</strong> {brand.isActive ? 'Active' : 'Inactive'}</p>
            </div>
            <div className="card-actions">
              {canManageAdmins && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => openBrandAdmin(brand)}
                >
                  Admin Login
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => handleEdit(brand)}
              >
                Edit
              </button>
              {canDeleteBrand && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(brand._id)}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        )}
        emptyState={brandEmptyState}
        scrollRef={scrollRef}
        footer={brandListFooter}
      />
      </>
      )}

      <PortalAdminCredentialsModal
        open={Boolean(adminTarget)}
        title={adminData?.id ? 'Update Brand Admin' : 'Add Brand Admin'}
        subtitle={adminTarget ? `Login for brand: ${adminTarget.name}` : ''}
        loading={adminLoading}
        saving={adminSaving}
        initialAdmin={adminData}
        onClose={closeBrandAdmin}
        onSave={saveBrandAdmin}
      />
      </div>
    </AdminLayout>
  );
};

export default BrandManagement;
